import { MetricsCards } from "@/components/dashboard/metrics-cards";
import { AlertsTimeline } from "@/components/dashboard/alerts-timeline";
import { RiskPyramid } from "@/components/dashboard/risk-pyramid";
import { NetworkVisualization } from "@/components/dashboard/network-visualization";
import { RiskFactors } from "@/components/dashboard/risk-factors";
import { GeographicDisplay } from "@/components/dashboard/geographic-display";
import { ScanDialog } from "@/components/scanning/scan-dialog";
import { CrawlSiteDialog } from "@/components/scanning/crawl-site-dialog";
import { RecentScans } from "@/components/dashboard/recent-scans";
import { JavaScriptScanner, JavaScriptScanResults } from "@/components/dashboard/javascript-scanner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Scan, Plus, Bell, Clock, AlertTriangle, Globe } from "lucide-react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

interface SubscriptionStatus {
  plan?: string;
  isTrial?: boolean;
  trialDaysLeft?: number;
  needsSubscription?: boolean;
  isExempt?: boolean;
  hasActiveSubscription?: boolean;
}

export default function Dashboard() {
  // Get subscription status to show trial days
  const { data: subscriptionStatus } = useQuery<SubscriptionStatus>({
    queryKey: ["/api/subscription/status"],
    refetchInterval: 60000, // Refetch every minute
  });

  const isTrial = subscriptionStatus?.isTrial;
  const trialDaysLeft = subscriptionStatus?.trialDaysLeft;
  const needsSubscription = subscriptionStatus?.needsSubscription;
  const isExempt = subscriptionStatus?.isExempt;

  return (
    <div className="space-y-6 min-h-full w-full">
      {/* Dashboard Header with Start Scan Button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Security Command Center
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Real-time security monitoring and threat intelligence dashboard
          </p>
        </div>
        <div className="flex gap-2">
          <ScanDialog>
            <Button className="flex items-center gap-2">
              <Scan className="h-4 w-4" />
              Start New Scan
            </Button>
          </ScanDialog>
          <CrawlSiteDialog>
            <Button variant="outline" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Crawl site
            </Button>
          </CrawlSiteDialog>
        </div>
      </div>

      {/* Trial Countdown Banner */}
      {isTrial && trialDaysLeft !== undefined && !isExempt && (
        <div className={`${
          trialDaysLeft <= 7 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
            : trialDaysLeft <= 14
            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        } border rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 ${
                trialDaysLeft <= 7 
                  ? 'bg-red-100 dark:bg-red-800' 
                  : trialDaysLeft <= 14
                  ? 'bg-orange-100 dark:bg-orange-800'
                  : 'bg-blue-100 dark:bg-blue-800'
              } rounded-full flex items-center justify-center`}>
                <Clock className={`h-4 w-4 ${
                  trialDaysLeft <= 7 
                    ? 'text-red-600 dark:text-red-400' 
                    : trialDaysLeft <= 14
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`} />
              </div>
              <div>
                <h3 className={`text-sm font-medium ${
                  trialDaysLeft <= 7 
                    ? 'text-red-900 dark:text-red-100' 
                    : trialDaysLeft <= 14
                    ? 'text-orange-900 dark:text-orange-100'
                    : 'text-blue-900 dark:text-blue-100'
                }`}>
                  {trialDaysLeft <= 0 
                    ? 'Trial Expired' 
                    : trialDaysLeft === 1
                    ? 'Trial Ends Tomorrow'
                    : `${trialDaysLeft} Days Left in Your Trial`
                  }
                </h3>
                <p className={`text-sm ${
                  trialDaysLeft <= 7 
                    ? 'text-red-700 dark:text-red-300' 
                    : trialDaysLeft <= 14
                    ? 'text-orange-700 dark:text-orange-300'
                    : 'text-blue-700 dark:text-blue-300'
                }`}>
                  {trialDaysLeft <= 0 
                    ? 'Your trial has ended. Subscribe now to continue using PCI Scan Report.'
                    : trialDaysLeft <= 7
                    ? 'Your trial is ending soon. Subscribe now to avoid service interruption.'
                    : 'Subscribe before your trial ends to continue enjoying unlimited access to all features.'
                  }
                </p>
              </div>
            </div>
            {needsSubscription && (
              <Link href="/subscription">
                <Button 
                  variant={trialDaysLeft && trialDaysLeft <= 0 ? "destructive" : "default"}
                  className="flex items-center gap-2"
                >
                  {trialDaysLeft && trialDaysLeft <= 0 ? (
                    <>
                      <AlertTriangle className="h-4 w-4" />
                      Subscribe Now
                    </>
                  ) : (
                    <>
                      View Plans
                    </>
                  )}
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* New User Banner */}
      {(!isTrial || isExempt) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-sm font-bold">💡</span>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Welcome to PCI Scan Report!
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Begin your security assessment to unlock comprehensive threat intelligence and monitoring capabilities.
              </p>
            </div>
          </div>
        </div>
      )}

      <MetricsCards />

      <div className="space-y-8 mt-8">
        {/* JavaScript Security Scanner */}
        <JavaScriptScanner />
        
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 space-y-6">
            <AlertsTimeline />
            <RecentScans />
          </div>

          <div className="xl:col-span-2 space-y-6">
            <JavaScriptScanResults />
            <NetworkVisualization />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RiskFactors />
          <GeographicDisplay />
        </div>

      </div>
      
      {/* Bottom spacing to prevent white space */}
      <div className="h-8"></div>
    </div>
  );
}